import type { CSSProperties, ReactNode } from 'react';

/**
 * Reusable skeleton-loading primitives (CLE-33).
 *
 * Every block uses the shared `.ui-skeleton` class (neutral shimmer defined in
 * index.css) so the colour stays a touch lighter than the surrounding card and
 * automatically adapts to the active theme. Composite components mirror the real
 * content's layout so there is no layout shift when data arrives.
 *
 * Wrap a finished view in <FadeIn> for the smooth skeleton → content transition.
 */

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/* ----------------------------------------------------------------------- */
/* Base primitive                                                          */
/* ----------------------------------------------------------------------- */

interface SkeletonProps {
  className?: string;
  /** Inline width override (e.g. '60%' or 120). */
  width?: string | number;
  /** Inline height override (e.g. '1rem' or 24). */
  height?: string | number;
  /** Render as a circle (avatars, icon placeholders). */
  circle?: boolean;
  style?: CSSProperties;
}

/** A single shimmering block. The building block for every other skeleton. */
export function Skeleton({ className, width, height, circle, style }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={cx('ui-skeleton block', circle && 'rounded-full', className)}
      style={{ width, height, ...style }}
    />
  );
}

/* ----------------------------------------------------------------------- */
/* SkeletonText — stacked text lines                                       */
/* ----------------------------------------------------------------------- */

interface SkeletonTextProps {
  /** Number of lines to render. */
  lines?: number;
  className?: string;
  /** Tailwind height class for each line. */
  lineClassName?: string;
  /** Shorten the final line so it reads like a paragraph. */
  lastLineWidth?: string;
}

export function SkeletonText({
  lines = 3,
  className,
  lineClassName = 'h-3.5',
  lastLineWidth = '70%',
}: SkeletonTextProps) {
  return (
    <div className={cx('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={lineClassName}
          width={i === lines - 1 && lines > 1 ? lastLineWidth : '100%'}
        />
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* SkeletonCard — a card placeholder (title, badge, body, footer)          */
/* ----------------------------------------------------------------------- */

interface SkeletonCardProps {
  className?: string;
  /** Show a progress-bar placeholder near the bottom. */
  showProgress?: boolean;
  /** Number of body text lines. */
  lines?: number;
}

export function SkeletonCard({ className, showProgress = false, lines = 2 }: SkeletonCardProps) {
  return (
    <div
      className={cx(
        'rounded-lg border border-border bg-card p-4 text-card-foreground shadow-sm',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <Skeleton className="h-5" width="55%" />
        <Skeleton className="h-5 rounded-full" width={64} />
      </div>
      <SkeletonText className="mt-4" lines={lines} />
      {showProgress && <Skeleton className="mt-4 h-2 rounded-full" width="100%" />}
    </div>
  );
}

/** A responsive grid of card placeholders. */
interface SkeletonCardGridProps {
  count?: number;
  className?: string;
  showProgress?: boolean;
}

export function SkeletonCardGrid({
  count = 6,
  className = 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3',
  showProgress = false,
}: SkeletonCardGridProps) {
  return (
    <div className={className}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} showProgress={showProgress} />
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* SkeletonTable — header row + body rows                                  */
/* ----------------------------------------------------------------------- */

interface SkeletonTableProps {
  rows?: number;
  columns?: number;
  className?: string;
}

export function SkeletonTable({ rows = 6, columns = 5, className }: SkeletonTableProps) {
  return (
    <div className={cx('overflow-hidden rounded-lg border border-border', className)}>
      {/* Header */}
      <div className="flex gap-4 border-b border-border bg-muted/40 px-4 py-3">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-3.5 flex-1" />
        ))}
      </div>
      {/* Body */}
      <div className="divide-y divide-border bg-card">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex items-center gap-4 px-4 py-3">
            {Array.from({ length: columns }).map((_, c) => (
              <Skeleton key={c} className="h-4 flex-1" width={c === 0 ? '80%' : undefined} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* SkeletonList — vertical list of rows (optionally with avatar/badge)     */
/* ----------------------------------------------------------------------- */

interface SkeletonListProps {
  rows?: number;
  className?: string;
  /** Render a leading avatar/icon placeholder per row. */
  avatar?: boolean;
  /** Render a trailing badge placeholder per row. */
  badge?: boolean;
}

export function SkeletonList({
  rows = 5,
  className,
  avatar = false,
  badge = false,
}: SkeletonListProps) {
  return (
    <div
      className={cx('divide-y divide-border rounded-lg border border-border bg-card', className)}
    >
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          {avatar && <Skeleton circle width={36} height={36} />}
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5" width="45%" />
            <Skeleton className="h-3" width="70%" />
          </div>
          {badge && <Skeleton className="h-5 rounded-full" width={56} />}
        </div>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* SkeletonChart — chart area with axis ticks + bars                       */
/* ----------------------------------------------------------------------- */

interface SkeletonChartProps {
  className?: string;
  /** Number of vertical bars to draw. */
  bars?: number;
  /** Height of the chart plot in px. */
  height?: number;
}

export function SkeletonChart({ className, bars = 10, height = 220 }: SkeletonChartProps) {
  // Deterministic pseudo-random heights so the bars look natural but stable.
  const heights = Array.from({ length: bars }, (_, i) => 35 + ((i * 37) % 60));
  return (
    <div className={cx('rounded-lg border border-border bg-card p-4', className)}>
      <div className="flex items-end gap-3" style={{ height }}>
        {heights.map((h, i) => (
          <Skeleton
            key={i}
            className="flex-1 rounded-sm rounded-b-none"
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
      <div className="mt-3 flex gap-3">
        {heights.map((_, i) => (
          <Skeleton key={i} className="h-2.5 flex-1" />
        ))}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* SkeletonStats — KPI widget row                                          */
/* ----------------------------------------------------------------------- */

interface SkeletonStatsProps {
  count?: number;
  className?: string;
}

export function SkeletonStats({
  count = 4,
  className = 'grid grid-cols-2 gap-4 lg:grid-cols-4',
}: SkeletonStatsProps) {
  return (
    <div className={className}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-lg border border-border bg-card p-4 shadow-sm">
          <Skeleton className="h-3" width="50%" />
          <Skeleton className="mt-3 h-7" width="70%" />
          <Skeleton className="mt-2 h-3" width="40%" />
        </div>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* FadeIn — smooth skeleton → content transition wrapper                   */
/* ----------------------------------------------------------------------- */

interface FadeInProps {
  children: ReactNode;
  className?: string;
}

/** Wrap real content so it fades in once loading completes. */
export function FadeIn({ children, className }: FadeInProps) {
  return <div className={cx('ui-fade-in', className)}>{children}</div>;
}
